import { IcMandatory12 } from '@assets/icons/12x12';
import {
  Button,
  Center,
  createListCollection,
  Fieldset,
  HStack,
  Icon,
  Input,
  RadioGroup,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Field as ChakraField } from '@chakra-ui/react/field';
import { CockpitFormLayout } from '@components/cockpit/layouts/CockpitFormLayout';
import { ZoneForm } from '@components/cockpit/panels/zoneManagement/zoneManagement.types';
import Field from '@components/common/form/Field';
import ModifiedTag from '@components/common/form/ModifiedField';
import { InputNumber } from '@components/ui/input/InputNumber';
import RequiredIndicator from '@components/ui/RequiredIndicator';
import { CustomSelect } from '@components/ui/select/Select';
import { Switch } from '@components/ui/switch';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectActiveGroupsPlatforms, selectUsableProcedureModels } from '@redux/config/config.selectors';
import { isNoFlyZoneType } from '@utils/map/zone.utils';
import { getIn, useFormikContext } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';

import { Zone2DTypeEnum, zoneLabelFromZone2DType } from '@/types/config/config.types';

type Props = {
  isNewZone: boolean;
  setShowMapForm: (value: boolean) => void;
};

export default function ZoneGeneralForm({ isNewZone, setShowMapForm }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const usablePlatforms = useAppSelector(selectActiveGroupsPlatforms);
  const usableProcedureModels = useAppSelector(selectUsableProcedureModels);

  const { errors, touched, initialValues, values, setFieldValue } = useFormikContext<ZoneForm>();

  const isInvalid = (key: keyof ZoneForm) => {
    const isTouched = getIn(touched, key);
    const error = getIn(errors, key);
    return Boolean(isTouched && error);
  };

  const hasChanged = (key: keyof ZoneForm) => {
    return values[key] !== initialValues[key];
  };

  return (
    <CockpitFormLayout title={`components.zones.form.${isNewZone ? 'creationTitle' : 'editionTitle'}`}>
      <Fieldset.Root invalid={Object.keys(errors).some((key) => touched[key as keyof typeof touched])}>
        <VStack width="100%" gap={5} alignItems="start">
          <ChakraField.Root variant="cockpit" required invalid={isInvalid('name')}>
            <ChakraField.Label width="100%" height="20px">
              <FormattedMessage id="components.zones.form.name" />
              <RequiredIndicator />
              {hasChanged('name') && <ModifiedTag />}
            </ChakraField.Label>
            <Field as={Input} type="text" name="name" id="name" />
          </ChakraField.Root>
          <ChakraField.Root variant="cockpit" required invalid={isInvalid('type')}>
            <ChakraField.Label width="100%" height="20px">
              <FormattedMessage id="components.zones.form.type" />
              <RequiredIndicator />
            </ChakraField.Label>
            <CustomSelect
              name="type"
              disabled={!isNewZone}
              collection={createListCollection({
                items: Object.values(Zone2DTypeEnum).map((value) => ({
                  label: formatMessage({ id: `filters.${zoneLabelFromZone2DType[value]}` }),
                  value,
                })),
              })}
              value={values.type}
              onChange={(value) => setFieldValue('type', value)}
            />
          </ChakraField.Root>
          {values.type && isNoFlyZoneType(values.type) && (
            <ChakraField.Root variant="cockpit" required invalid={isInvalid('platform')}>
              <ChakraField.Label width="100%" height="20px">
                <FormattedMessage id="components.zones.form.platform" />
                <RequiredIndicator />
              </ChakraField.Label>
              <CustomSelect
                name="platform"
                collection={createListCollection({
                  items: usablePlatforms.map((platform) => ({
                    label: platform.name,
                    value: platform.code,
                  })),
                })}
                value={values.platform}
                onChange={(value) => setFieldValue('platform', value)}
              />
            </ChakraField.Root>
          )}
          <ChakraField.Root variant="cockpit" width="650px" invalid={isInvalid('geometry')}>
            <ChakraField.Label width="100%" height="20px">
              <FormattedMessage id="components.zones.form.geometry.title" />
            </ChakraField.Label>
            <RadioGroup.Root
              name="geometry"
              invalid={isInvalid('geometry')}
              value={values.geometry}
              size="screenStd"
              onValueChange={({ value }) => setFieldValue('geometry', value)}
            >
              <RadioGroup.Item key="INFINITE_HEIGHT" value="INFINITE_HEIGHT">
                <RadioGroup.ItemHiddenInput />
                <RadioGroup.ItemIndicator />
                <RadioGroup.ItemText>
                  <FormattedMessage id="components.zones.form.geometry.INFINITE_HEIGHT" />
                </RadioGroup.ItemText>
              </RadioGroup.Item>
              <RadioGroup.Item key="FINITE_HEIGHT" value="FINITE_HEIGHT">
                <RadioGroup.ItemHiddenInput />
                <RadioGroup.ItemIndicator />
                <RadioGroup.ItemText>
                  <FormattedMessage id="components.zones.form.geometry.FINITE_HEIGHT" />
                </RadioGroup.ItemText>
              </RadioGroup.Item>
            </RadioGroup.Root>
          </ChakraField.Root>

          {values.type && isNoFlyZoneType(values.type) && (
            <ChakraField.Root variant="cockpit" required invalid={isInvalid('procedureModel')}>
              <ChakraField.Label width="100%" height="20px">
                <FormattedMessage id="components.zones.form.procedureModel" />
                <RequiredIndicator />
              </ChakraField.Label>
              <CustomSelect
                name="type"
                placeholder={formatMessage({ id: 'components.zones.form.noProcedureModel' })}
                collection={createListCollection({
                  items: usableProcedureModels.map((procedureModel) => ({
                    label: procedureModel.name,
                    value: procedureModel.id.toString(),
                  })),
                })}
                value={values.procedureModel?.id.toString()}
                onChange={(value) =>
                  setFieldValue(
                    'procedureModel',
                    usableProcedureModels.find((procedureModel) => procedureModel.id.toString() === value),
                  )
                }
              />
            </ChakraField.Root>
          )}
          {values.type && values.type === Zone2DTypeEnum.IMZ && (
            <ChakraField.Root variant="cockpit" required invalid={isInvalid('marginSize')}>
              <ChakraField.Label width="100%" height="20px">
                <FormattedMessage id="components.zones.form.marginSize" />
                <RequiredIndicator />
                {hasChanged('marginSize') && <ModifiedTag />}
              </ChakraField.Label>
              <Field
                as={InputNumber}
                type="text"
                name="marginSize"
                id="marginSize"
                allowMouseWheel
                onChange={(value?: number) => setFieldValue('marginSize', value)}
                suffix="m"
              />
            </ChakraField.Root>
          )}
          <HStack padding={4} width="100%" border="2px solid" borderColor="neutral.850">
            <Text>
              <FormattedMessage id="components.zones.form.activate" />
            </Text>
            <Switch
              size="sm"
              marginLeft="auto"
              checked={values.isZoneActive}
              onCheckedChange={() => {
                void setFieldValue('isZoneActive', !values.isZoneActive);
              }}
            />
          </HStack>

          <VStack gap={2} width="100%" alignItems="start">
            <HStack>
              <Text color="neutral.300">
                <FormattedMessage id="components.zones.form.volumeDefinition" />
              </Text>
              <Icon as={IcMandatory12} boxSize="12px" />
            </HStack>

            <Center width="100%" padding={4} border="2px solid" borderColor="neutral.850">
              <Button variant="toggle" size="toggleM" flex={1} data-active onClick={() => setShowMapForm(true)}>
                <FormattedMessage id="components.zones.form.draw" />
              </Button>
            </Center>
          </VStack>
        </VStack>
      </Fieldset.Root>
    </CockpitFormLayout>
  );
}
